import { Link } from 'react-router-dom';
import { Calendar, Eye } from 'lucide-react';

const categoryColors = {
  'news': '#3B82F6',
  'personal-finance': '#10B981',
  'debt-management': '#EF4444',
  'small-business': '#8B5CF6',
  'investing': '#F59E0B',
  'markets': '#0A192F',
};

const defaultImage = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80';

export function ArticleCardHero({ article }) {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Link
      to={`/article/${article.id}`}
      className="group relative block h-full min-h-[280px] sm:min-h-[350px] lg:min-h-[500px] overflow-hidden rounded-none"
      data-testid={`article-hero-${article.id}`}
    >
      <img
        src={article.image_url || defaultImage}
        alt={article.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
        onError={(e) => { e.target.src = defaultImage; }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/95 via-[#0A192F]/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
        <span
          className="inline-block text-[10px] sm:text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-none mb-2 sm:mb-3 border"
          style={{ borderColor: `${categoryColors[article.category] || '#C5A059'}60`, color: '#C5A059', backgroundColor: 'rgba(10,25,47,0.4)' }}
        >
          {article.category?.replace('-', ' ')}
        </span>
        <h2
          className="text-lg sm:text-2xl lg:text-4xl text-white mb-2 sm:mb-3 leading-tight group-hover:text-[#C5A059] transition-colors line-clamp-3"
          style={{ fontFamily: "'Libre Baskerville', serif" }}
        >
          {article.title}
        </h2>
        <p className="hidden sm:block text-white/60 text-sm lg:text-base line-clamp-2 mb-4 max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {article.excerpt?.replace(/<[^>]*>/g, '')}
        </p>
        <div className="flex items-center space-x-3 sm:space-x-4 text-[10px] sm:text-xs text-white/40 font-mono">
          <span className="flex items-center">
            <Calendar className="w-3 h-3 mr-1" strokeWidth={1.5} />
            {formatDate(article.published_at)}
          </span>
          <span className="flex items-center">
            <Eye className="w-3 h-3 mr-1" strokeWidth={1.5} />
            {article.view_count || 0} views
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCardStandard({ article }) {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Link
      to={`/article/${article.id}`}
      className="group article-card flex flex-col sm:flex-row bg-white border border-[#E2E8F0] rounded-none overflow-hidden transition-all duration-300"
      data-testid={`article-card-${article.id}`}
    >
      <div className="sm:w-[280px] lg:w-[320px] flex-shrink-0 overflow-hidden aspect-[16/10] sm:aspect-auto sm:min-h-[180px]">
        <img
          src={article.image_url || defaultImage}
          alt={article.title}
          className="w-full h-full object-cover article-image"
          loading="lazy"
          onError={(e) => { e.target.src = defaultImage; }}
        />
      </div>
      <div className="p-4 sm:p-5 flex flex-col justify-between flex-1 min-w-0">
        <div>
          <span
            className="inline-block text-[10px] sm:text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-none mb-2 sm:mb-3"
            style={{ backgroundColor: `${categoryColors[article.category] || '#0A192F'}08`, color: categoryColors[article.category] || '#0A192F', border: `1px solid ${categoryColors[article.category] || '#0A192F'}20` }}
          >
            {article.category?.replace('-', ' ')}
          </span>
          <h3
            className="text-base sm:text-lg text-[#1A1A1A] mb-2 leading-snug group-hover:text-[#C5A059] transition-colors line-clamp-2"
            style={{ fontFamily: "'Libre Baskerville', serif" }}
          >
            {article.title}
          </h3>
          <p className="hidden sm:block text-[#64748B] text-sm line-clamp-2 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {article.excerpt?.replace(/<[^>]*>/g, '')}
          </p>
        </div>
        <div className="flex items-center justify-between text-[10px] sm:text-xs text-[#64748B] font-mono mt-auto">
          <span>{formatDate(article.published_at)}</span>
          <span className="flex items-center">
            <Eye className="w-3 h-3 mr-1" strokeWidth={1.5} />
            {article.view_count || 0}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCardCompact({ article, index }) {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex items-start space-x-4 py-4 border-b border-[#E2E8F0] last:border-0"
      data-testid={`article-compact-${article.id}`}
    >
      {index !== undefined && (
        <span className="font-mono text-3xl font-bold text-[#C5A059]/25 w-8 flex-shrink-0">
          {String(index + 1).padStart(2, '0')}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <h4
          className="text-sm text-[#1A1A1A] group-hover:text-[#C5A059] transition-colors line-clamp-2 leading-snug"
          style={{ fontFamily: "'Libre Baskerville', serif" }}
        >
          {article.title}
        </h4>
        <div className="flex items-center space-x-2 mt-2 text-xs text-[#64748B] font-mono">
          <span>{formatDate(article.published_at)}</span>
          <span className="w-1 h-1 rounded-full bg-[#C5A059]/40" />
          <span>{article.view_count || 0} views</span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCardSidebar({ article }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex space-x-3"
      data-testid={`article-sidebar-${article.id}`}
    >
      <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-none">
        <img
          src={article.image_url || defaultImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = defaultImage; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <h4
          className="text-sm text-[#1A1A1A] group-hover:text-[#C5A059] transition-colors line-clamp-2 leading-snug"
          style={{ fontFamily: "'Libre Baskerville', serif" }}
        >
          {article.title}
        </h4>
        <span className="text-xs text-[#64748B] font-mono mt-1 block">
          {new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>
    </Link>
  );
}
